import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import ProBadge from '@/components/ProBadge';
import {
  Calendar, ListChecks, Settings as Gear, Bell, Users,
  Activity, BookOpen, Shield, Brain, ChevronDown, User, LogOut, Sparkles, Plane, Crown
} from 'lucide-react';
import NotificationPanel from '@/components/NotificationPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// "You" mega-menu items — all insight/self features consolidated
const FREE_ITEMS = [
  { path: '/trips',            icon: Plane,     label: 'Trips & Events',   desc: 'Plan around travel & big days', color: '#fbbf24' },
  { path: '/reflection',       icon: BookOpen,  label: 'Weekly Reflect',   desc: 'Look back, set intentions', color: '#a78bfa' },
  { path: '/calendar-sync',    icon: Calendar,  label: 'Calendar Sync',    desc: 'Google, Outlook, Apple',    color: '#ec4899' },
];
const PRO_ITEMS = [
  { path: '/non-negotiables',  icon: Shield,    label: 'Non-Negotiables',  desc: 'Protect what matters most', color: '#22c55e', premium: true },
  { path: '/health',           icon: Activity,  label: 'Schedule Health',  desc: 'Burnout & load analysis',   color: '#00D9FF', premium: true },
  { path: '/life-report',      icon: Brain,     label: 'Life Intelligence', desc: 'Monthly AI life coach',    color: '#f59e0b', premium: true },
];
const YOU_ITEMS = [...FREE_ITEMS, ...PRO_ITEMS];


const Navbar = () => {
  const { auth, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showYou, setShowYou] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const youRef = useRef();
  const userRef = useRef();

  useEffect(() => {
    loadPending();
    const t = setInterval(loadPending, 30000);
    return () => clearInterval(t);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (youRef.current && !youRef.current.contains(e.target)) setShowYou(false);
      if (userRef.current && !userRef.current.contains(e.target)) setShowUser(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadPending = async () => {
    try {
      const res = await axios.get(`${API}/friends/requests`, { headers: { Authorization: `Bearer ${auth.token}` } });
      setPendingCount(res.data.length);
    } catch {}
  };

  const isActive = (path) => location.pathname === path;
  const youActive = YOU_ITEMS.some(i => location.pathname === i.path);

  // Core nav — just 4 items
  const coreNav = [
    { path: '/dashboard', label: 'Home',    icon: Calendar },
    { path: '/tasks',     label: 'Tasks',   icon: ListChecks },
    { path: '/friends',   label: 'Friends', icon: Users },
  ];

  return (
    <nav
      className="sticky top-0 z-50 border-b"
      style={{
        background: 'rgba(10,14,39,0.96)',
        backdropFilter: 'blur(20px)',
        borderColor: 'rgba(255,255,255,0.07)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-15" style={{ height: 60 }}>

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2.5 flex-shrink-0">
            <img src="/logo.png" alt="SpaceCal logo" data-testid="navbar-logo"
              className="w-9 h-9 rounded-xl object-cover" style={{ boxShadow: '0 2px 12px rgba(139,92,246,0.4)' }}/>
            <span className="heading-font text-lg font-bold text-white tracking-tight">SpaceCal</span>
            <span className="text-xs text-cyan-400 font-semibold">.ai</span>
          </Link>

          {/* Core nav */}
          <div className="flex items-center gap-1">
            {coreNav.map(({ path, label, icon: Icon }) => (
              <Link key={path} to={path}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${
                  isActive(path)
                    ? 'text-cyan-400 bg-cyan-400/10'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}>
                <Icon size={16}/>
                {label}
                {path === '/friends' && pendingCount > 0 && (
                  <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </Link>
            ))}

            {/* You dropdown */}
            <div className="relative" ref={youRef}>
              <button
                onClick={() => setShowYou(o => !o)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${
                  youActive
                    ? 'text-cyan-400 bg-cyan-400/10'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}>
                <Sparkles size={16}/>
                Insights
                <ChevronDown size={13} className={`transition-transform ${showYou ? 'rotate-180' : ''}`}/>
              </button>

              {showYou && (
                <div className="absolute top-full left-0 mt-2 w-72 rounded-2xl overflow-hidden z-50 py-2"
                  style={{ background: 'rgba(10,14,39,0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
                  {[['Insights', FREE_ITEMS], ['Pro features', PRO_ITEMS]].map(([group, list]) => (
                    <div key={group} data-testid={`nav-group-${group === 'Pro features' ? 'pro' : 'free'}`}
                      className={group === 'Pro features' ? 'mt-1 pt-2 border-t border-yellow-400/15' : ''}>
                      <p className={`px-4 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 ${group === 'Pro features' ? 'text-yellow-400/90' : 'text-gray-600'}`}>
                        {group === 'Pro features' && <Crown size={11}/>}{group}
                      </p>
                  {list.map(({ path, icon: Icon, label, desc, color, premium }) => (
                    <Link key={path} to={path} data-testid={`nav-item-${path.slice(1)}`}
                      onClick={() => setShowYou(false)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
                        <Icon size={15} style={{ color }}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{label}</span>
                          {premium && <ProBadge testId={`pro-badge-${path.slice(1)}`}/>}
                        </div>
                        <span className="text-xs text-gray-500">{desc}</span>
                      </div>
                    </Link>
                  ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(o => !o)}
                className="relative w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all">
                <Bell size={18}/>
                {pendingCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500"/>
                )}
              </button>
              {showNotifications && (
                <NotificationPanel onClose={() => setShowNotifications(false)} onRequestsChange={loadPending}/>
              )}
            </div>

            {/* Settings */}
            <Link to="/settings"
              className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all ${
                isActive('/settings') ? 'text-cyan-400 bg-cyan-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}>
              <Gear size={18}/>
            </Link>

            {/* User avatar / menu */}
            <div className="relative ml-1" ref={userRef}>
              <button onClick={() => setShowUser(o => !o)}
                className="flex items-center gap-2 pl-3 border-l border-white/8 hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                  style={{ background: 'linear-gradient(135deg,#00D9FF22,#a78bfa22)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  {auth.user?.name?.charAt(0).toUpperCase()}
                </div>
              </button>
              {showUser && (
                <div className="absolute top-full right-0 mt-2 w-52 rounded-2xl overflow-hidden z-50 py-2"
                  style={{ background: 'rgba(10,14,39,0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
                  <div className="px-4 py-3 border-b border-white/8">
                    <p className="text-sm font-semibold text-white">{auth.user?.name}</p>
                    <p className="text-xs text-gray-500 truncate">{auth.user?.email}</p>
                  </div>
                  <Link to="/settings" onClick={() => setShowUser(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
                    <Gear size={15}/> Settings
                  </Link>
                  <button onClick={logout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-400/5 transition-colors">
                    <LogOut size={15}/> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </nav>
  );
};

export default Navbar;
